import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, GraduationCap, Search, Filter, X } from 'lucide-react'
import { MarketplaceHeader } from '@/components/MarketplaceHeader'
import { PreferencesBanner } from '@/components/PreferencesBanner'
import { UserPreferencesModal } from '@/components/UserPreferencesModal'
import { ArenaCard } from '@/components/ArenaCard'
import { ArenaFilterModal } from '@/components/ArenaFilterModal'
import { SuggestArenaEmptyState } from '@/components/SuggestArenaEmptyState'
import { Arena } from '@/types/arena'
import { arenaService, type VenueSearchItem } from '@/services/arenaService'
import { modalitiesManager } from '@/managers/modalitiesManager'
import { amenitiesManager } from '@/managers/amenitiesManager'
import { Modality } from '@/types/modality'
import { Amenity } from '@/types/amenity'
import type { SportModality } from '@/types/address'
import { useAuth } from '@/contexts/AuthContext'
import { useUserPreferences } from '@/hooks/useUserPreferences'
import { useUserLocation } from '@/hooks/useUserLocation'
import { analyticsService } from '@meiuca/analytics'

export function ArenaSearchPage() {
  console.log('[ArenaSearchPage] Component mounted/rendered')

  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const { savePreferences, shouldShowModal } = useUserPreferences({
    autoLoad: true,
    isAuthenticated
  })
  const { userLocation } = useUserLocation()
  const coordinate = userLocation?.coordinate
  const hasCoordinates = !!coordinate && (coordinate.latitude !== 0 || coordinate.longitude !== 0)
  const coordinateKey = hasCoordinates ? `${coordinate!.latitude},${coordinate!.longitude}` : null

  // Track screen view on mount
  useEffect(() => {
    analyticsService.trackScreenView('arena_search')
  }, [])

  const [searchQuery, setSearchQuery] = useState('')
  const [venues, setVenues] = useState<VenueSearchItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Filter states
  const [modalities, setModalities] = useState<Modality[]>([])
  const [amenities, setAmenities] = useState<Amenity[]>([])
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)
  const [selectedModalities, setSelectedModalities] = useState<Set<string>>(new Set())
  const [selectedAmenities, setSelectedAmenities] = useState<Set<string>>(new Set())
  const [tempSelectedModalities, setTempSelectedModalities] = useState<Set<string>>(new Set())
  const [tempSelectedAmenities, setTempSelectedAmenities] = useState<Set<string>>(new Set())

  // Preferences modal
  const [preferencesModalOpen, setPreferencesModalOpen] = useState(false)
  const showBanner = shouldShowModal

  console.log('[ArenaSearchPage] Current state:', { searchQuery, venuesCount: venues.length, isLoading, error })

  // Load modalities and amenities on mount
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [loadedModalities, loadedAmenities] = await Promise.all([
          modalitiesManager.getModalities(),
          amenitiesManager.getAmenities(),
        ])
        setModalities(loadedModalities)
        setAmenities(loadedAmenities)
      } catch (err) {
        console.error('[ArenaSearchPage] Error loading filters:', err)
      }
    }
    loadFilters()
  }, [])

  // Load venues on mount and when search query or filters change (debounced)
  useEffect(() => {
    console.log('[ArenaSearchPage] useEffect triggered, searchQuery:', searchQuery, 'selectedModalities:', selectedModalities, 'selectedAmenities:', selectedAmenities)

    const timeoutId = setTimeout(() => {
      console.log('[ArenaSearchPage] Debounce timeout finished, calling loadVenues')
      loadVenues()
    }, 300) // 300ms debounce

    return () => {
      console.log('[ArenaSearchPage] Cleanup: clearing timeout')
      clearTimeout(timeoutId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, selectedModalities, selectedAmenities, coordinateKey])

  const loadVenues = async () => {
    try {
      console.log('[ArenaSearchPage] Loading venues with query:', searchQuery, 'modalities:', selectedModalities, 'amenities:', selectedAmenities)
      setIsLoading(true)
      setError(null)

      const params = {
        query: searchQuery || undefined,
        lat: hasCoordinates ? coordinate!.latitude : undefined,
        lng: hasCoordinates ? coordinate!.longitude : undefined,
        modalities: selectedModalities.size > 0 ? Array.from(selectedModalities) : undefined,
        amenities: selectedAmenities.size > 0 ? Array.from(selectedAmenities) : undefined,
      }
      console.log('[ArenaSearchPage] Calling arenaService.searchVenues with params:', params)

      const response = await arenaService.searchVenues(params)

      console.log('[ArenaSearchPage] Response received:', response)
      setVenues(response.venues)

      // Track search performed
      analyticsService.track('arena_search_search_performed', {
        search_query: searchQuery || '(empty)',
        modalities_count: selectedModalities.size,
        amenities_count: selectedAmenities.size,
        modalities: selectedModalities.size > 0 ? Array.from(selectedModalities).join(',') : undefined,
        amenities: selectedAmenities.size > 0 ? Array.from(selectedAmenities).join(',') : undefined,
        results_count: response.venues.length
      })
    } catch (err) {
      console.error('[ArenaSearchPage] Error loading venues:', err)
      setError('Erro ao carregar arenas. Tente novamente.')
    } finally {
      setIsLoading(false)
    }
  }

  // Convert VenueSearchItem to Arena for ArenaCard
  const arenas: Arena[] = venues.map((venue) => ({
    id: venue.id,
    slug: venue.slug,
    name: venue.name,
    logo: venue.logoUrl,
    coverImage: venue.coverUrl || '',
    modalities: [], // TODO: Get modalities from venue
    location: [venue.city, venue.state].filter(Boolean).join(', ') || 'Localização não informada',
    distance: venue.distance ? venue.distance * 1000 : 0, // Convert km to meters
    address: venue.address || '',
    coordinate: venue.latitude && venue.longitude
      ? {
          latitude: venue.latitude,
          longitude: venue.longitude,
        }
      : undefined,
  }))

  const handleArenaClick = (arena: Arena) => {
    // Track arena card click
    analyticsService.track('arena_search_card_clicked', {
      arena_id: arena.id,
      arena_name: arena.name,
      target_url: `/arena/${arena.slug ?? arena.id}`
    })

    navigate(`/arena/${arena.slug ?? arena.id}`)
  }

  const handleOpenFilterModal = () => {
    // Track filter modal opened
    analyticsService.track('arena_search_filter_opened', {})

    setTempSelectedModalities(new Set(selectedModalities))
    setTempSelectedAmenities(new Set(selectedAmenities))
    setIsFilterModalOpen(true)
  }

  const handleCloseFilterModal = () => {
    setIsFilterModalOpen(false)
  }

  const handleApplyFilters = (modalityIds: Set<string>, amenityIds: Set<string>) => {
    console.log('[ArenaSearchPage] Applying filters:', { modalityIds, amenityIds })

    // Track filters applied
    analyticsService.track('arena_search_filter_applied', {
      modalities_count: modalityIds.size,
      amenities_count: amenityIds.size,
      modalities: modalityIds.size > 0 ? Array.from(modalityIds).join(',') : undefined,
      amenities: amenityIds.size > 0 ? Array.from(amenityIds).join(',') : undefined
    })

    setSelectedModalities(new Set(modalityIds))
    setSelectedAmenities(new Set(amenityIds))
    setIsFilterModalOpen(false)
  }

  const handleClearFilters = () => {
    console.log('[ArenaSearchPage] Clearing filters')

    // Track filters cleared
    analyticsService.track('arena_search_filter_cleared', {
      previous_modalities_count: selectedModalities.size,
      previous_amenities_count: selectedAmenities.size
    })

    setTempSelectedModalities(new Set())
    setTempSelectedAmenities(new Set())
    setSelectedModalities(new Set())
    setSelectedAmenities(new Set())
    setIsFilterModalOpen(false)
  }

  const handleRemoveFilter = (type: 'modality' | 'amenity', id: string, label: string) => {
    // Track individual filter removed
    analyticsService.track('arena_search_filter_removed', {
      filter_type: type,
      filter_id: id,
      filter_label: label
    })

    if (type === 'modality') {
      const newSet = new Set(selectedModalities)
      newSet.delete(id)
      setSelectedModalities(newSet)
    } else {
      const newSet = new Set(selectedAmenities)
      newSet.delete(id)
      setSelectedAmenities(newSet)
    }
  }

  const handleClearAllFilters = () => {
    // Track clear all filters
    analyticsService.track('arena_search_filter_cleared', {
      previous_modalities_count: selectedModalities.size,
      previous_amenities_count: selectedAmenities.size,
      source: 'clear_all_button'
    })

    setSelectedModalities(new Set())
    setSelectedAmenities(new Set())
  }

  const handlePreferencesSave = async (modalities: SportModality[]) => {
    await savePreferences(modalities)
    setPreferencesModalOpen(false)
  }

  return (
    <div className="min-h-screen bg-background-secondary">
      <MarketplaceHeader onLocationClick={() => {}} />

      {/* Preferences Banner */}
      {showBanner && (
        <PreferencesBanner onOpenModal={() => setPreferencesModalOpen(true)} />
      )}

      {/* User Preferences Modal */}
      <UserPreferencesModal
        isOpen={preferencesModalOpen}
        onClose={() => setPreferencesModalOpen(false)}
        onSave={handlePreferencesSave}
        title="Quais esportes você pratica?"
        description="Conte pra gente para vermos as melhores arenas para você"
        allowSkip={true}
      />

      {/* Search Bar - Fixed below header */}
      <div className="sticky top-16 z-40 bg-background-secondary border-b border-border-light shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex gap-3">
            {/* Search Input */}
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-tertiary" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar por nome ou localização..."
                className="w-full pl-12 pr-4 py-3 border border-border-primary rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent text-text-primary placeholder:text-text-tertiary"
              />
            </div>

            {/* Filter Button */}
            <button
              onClick={handleOpenFilterModal}
              className="flex items-center gap-2 px-4 py-3 border border-border-primary rounded-lg hover:bg-background-tertiary transition-colors relative"
            >
              <Filter className="w-5 h-5 text-text-secondary" />
              <span className="text-text-primary font-medium">Filtros</span>
              {(selectedModalities.size > 0 || selectedAmenities.size > 0) && (
                <span className="absolute -top-1 -right-1 bg-brand-primary text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {selectedModalities.size + selectedAmenities.size}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Active Filters */}
        {(selectedModalities.size > 0 || selectedAmenities.size > 0) && (
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <span className="text-sm text-text-secondary font-medium">Filtros ativos:</span>
            {Array.from(selectedModalities).map((modalityId) => {
              const modality = modalities.find((m) => m.id === modalityId)
              if (!modality) return null

              return (
                <button
                  key={`modality-${modalityId}`}
                  onClick={() => handleRemoveFilter('modality', modalityId, modality.description)}
                  className="flex items-center gap-1 px-3 py-1 bg-brand-primary/10 text-brand-primary rounded-full text-sm font-medium hover:bg-brand-primary/20 transition-colors"
                >
                  {modality.description}
                  <X className="w-3 h-3" />
                </button>
              )
            })}
            {Array.from(selectedAmenities).map((amenityId) => {
              const amenity = amenities.find((a) => a.id === amenityId)
              if (!amenity) return null

              return (
                <button
                  key={`amenity-${amenityId}`}
                  onClick={() => handleRemoveFilter('amenity', amenityId, amenity.label)}
                  className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium hover:bg-green-200 transition-colors"
                >
                  {amenity.label}
                  <X className="w-3 h-3" />
                </button>
              )
            })}
            <button
              onClick={handleClearAllFilters}
              className="text-sm text-text-tertiary hover:text-text-primary underline"
            >
              Limpar tudo
            </button>
          </div>
        )}

        {/* Results */}
        <div className="mb-4">
          <p className="text-sm text-text-secondary">
            {isLoading
              ? 'Buscando...'
              : `${arenas.length} ${arenas.length === 1 ? 'arena encontrada' : 'arenas encontradas'}`}
          </p>
        </div>

        {/* Error State */}
        {error && (
          <div className="text-center py-12">
            <p className="text-red-600 mb-2">{error}</p>
            <button
              onClick={loadVenues}
              className="text-sm text-brand-primary hover:underline"
            >
              Tentar novamente
            </button>
          </div>
        )}

        {/* Arena Grid */}
        {!error && arenas.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {arenas.map((arena) => (
              <ArenaCard key={arena.id} arena={arena} onClick={() => handleArenaClick(arena)} />
            ))}
          </div>
        )}

        {/* Empty State */}
        {!error && !isLoading && arenas.length === 0 && (
          <SuggestArenaEmptyState
            title="Nenhuma arena encontrada"
            description="Não achamos arenas para sua busca. Conhece uma na sua região? Indique para a gente e ajude outros atletas a encontrarem onde jogar."
          />
        )}

        {/* Quem procura quadra é público candidato a aula — a ponte fica aqui,
            depois dos resultados, sem competir com eles. */}
        {!isLoading && (
          <button
            type="button"
            onClick={() => {
              analyticsService.track('ct_cross_sell_clicked', { from: 'arena_search' })
              navigate('/cts/search')
            }}
            className="mt-8 flex w-full items-center gap-4 rounded-xl border border-border-light bg-background-white p-5 text-left transition-shadow hover:shadow-md"
          >
            <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-brand-primary/10">
              <GraduationCap className="h-6 w-6 text-brand-primary" />
            </span>

            <span className="flex-1">
              <span className="block font-semibold text-text-primary">
                Procurando aula, não quadra?
              </span>
              <span className="block text-sm text-text-secondary">
                Veja os centros de treinamento, com horários das turmas e vagas.
              </span>
            </span>

            <ArrowRight className="h-5 w-5 flex-shrink-0 text-text-tertiary" />
          </button>
        )}
      </div>

      {/* Filter Modal */}
      <ArenaFilterModal
        open={isFilterModalOpen}
        onClose={handleCloseFilterModal}
        modalities={modalities}
        amenities={amenities}
        selectedModalityIds={tempSelectedModalities}
        selectedAmenityIds={tempSelectedAmenities}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
      />
    </div>
  )
}
